import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Typography, Box, Modal, Button } from "@mui/material";
import './CardPieces.scss';
import TwoChoiceCard from "./TwoChoiceCard";
import LockIcon from '@mui/icons-material/Lock';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import SingleInput from "./SingleInput";
import MultipleChoiceCard from "./MultipleChoiceCard";
import DropdownCard from "./DropdownCard";
import MatchUpDisplay from "./MatchUpDisplay";
import { useTheme } from "@emotion/react";
import useMediaQuery from '@mui/material/useMediaQuery';


const QuickPickCard = ({ 
    userPicksFromDB, 
    pickState, 
    setPickState, 
    prop, 
    interval, 
    selectedGameId, 
    hasGameStarted, 
    isGameFinal, 
    displayIndex, 
    customBackgroundColor, 
    customTextColor, 
    customBorderColor, 
    propTipColor = '#002129', 
    restrictedPicks = false, 
    propIdsArray, 
    freePickCount, 
    isUserPaid,
    setRestrictedModalOpen, 
}) => {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [modalOpen, setModalOpen] = useState(false);

    // Memoize prop destructuring to prevent repeated property access
    const propData = useMemo(() => ({
        id: prop?.id,
        propChoice: prop?.propChoice,
        propText: prop?.propText,
        options: prop?.options,
        optionOne: prop?.options?.[0],
        optionTwo: prop?.options?.[1],
        line: prop?.line,
        actualTotal: prop?.actualTotal,
        correctOption: prop?.correctOption,
        isTiebreaker: prop?.tiebreaker,
        points: prop?.points,
        league: prop?.league,
        homeTeamId: prop?.homeTeamId,
        awayTeamId: prop?.awayTeamId,
        middleDisplay: prop?.middleDisplay,
        propTip: prop?.propTip,
        propHeader: prop?.propHeader,
        modalInfo: prop?.modalInfo,
        highlightProp: prop?.highlight,
        highlightColor: prop?.highlightColor || '#CC293C',
        logo: prop?.logo
    }), [prop]);

    const globalGameId = selectedGameId;

    const topHeaderDisplay = useMemo(() => {
        const result = propData.propHeader || `Prop #${displayIndex}`;
        return result;
    }, [propData.propHeader, displayIndex]);

    console.log("Prop Header: ", propData.propHeader, "Prop Tip: ", propData.propTip, "Top Header Display: ", topHeaderDisplay);

    const canUserMakePick = useMemo(() => {
        if (!restrictedPicks || isUserPaid) {
            // If there are no restrictions or the user is paid, they can always make a pick
            return true;
        }
        if (freePickCount > 0) {
            //console.log("Free Pick Count: ", freePickCount);
            // Count how many picks the user has in pickState (total picks made)
            const totalPicksMade = pickState?.length || 0;
            // Check if the user has made less than the allowed number of picks
            return totalPicksMade < freePickCount;
        } else if (restrictedPicks && propIdsArray) {
            // Check how many picks user has made for this game
            const userPicksForGame = pickState?.filter(pick => pick.gameId === globalGameId) || [];
            const thisGameProps = propIdsArray?.find(prop => prop.gameId === globalGameId);
            const freePicksForThisGame = thisGameProps?.freeProps || 0;
            const totalPropsForThisGame = thisGameProps?.totalProps || 0;
            
            //console.log("User Picks for Game: ", userPicksForGame, "Free Picks for This Game: ", freePicksForThisGame, "Total Props: ", totalPropsForThisGame, "This Game Props: ", thisGameProps);
            
            // Case 1: If freeProps === totalProps, user can always make picks
            if (freePicksForThisGame === totalPropsForThisGame) {
                return true;
            }
            
            // Case 2: If freeProps < totalProps, user can make picks up to the free limit
            // This allows changing existing picks but not exceeding the free limit
            return userPicksForGame.length < freePicksForThisGame;
        }
    }, [pickState, globalGameId, freePickCount, restrictedPicks, propIdsArray, isUserPaid]);

    const [isPropSelected, setIsPropSelected] = useState(false);
    const [propInfo, setPropInfo] = useState({});
    const [isCurrentSelectionSaved, setIsCurrentSelectionSaved] = useState(null);

    useEffect(() => {
        const isPropSelected = pickState?.find(pick => pick.gameId === globalGameId && pick.id === propData.id);
        //console.log("Inside Use Effect", isPropSelected)
        setIsPropSelected(!!isPropSelected);
        setPropInfo(isPropSelected);
    }, [pickState, globalGameId, propData.id]);

    // Memoize saved prop lookup
    const savedProp = useMemo(() => 
        userPicksFromDB?.find(pick => pick.gameId === globalGameId && pick.id === propData.id),
        [userPicksFromDB, globalGameId, propData.id]
    );

    // New useEffect to determine if the current selection is saved
    useEffect(() => {
        if (savedProp && propInfo) {
            const isSaved = savedProp.option === propInfo.option; // Compare only the 'option' key
            setIsCurrentSelectionSaved(isSaved);
            //console.log("Is Current selection saved:", isSaved);
        } else {
            setIsCurrentSelectionSaved(false); // or set to null depending on your logic
        }
    }, [savedProp, propInfo]);

    // Memoize result and margin calculations
    const resultData = useMemo(() => ({
        result: savedProp?.result || "Pending",
        margin: savedProp?.margin
    }), [savedProp]);

    const capitalizeFirstLetter = useCallback((str) => 
        str ? str.charAt(0).toUpperCase() + str.slice(1) : '', []
    );

    // Memoize pick status calculations
    const pickStatusData = useMemo(() => {
        const pickStatusText = !isPropSelected ? 'No Pick' :
            (isPropSelected && !isCurrentSelectionSaved) ? 'Pending (Not Saved)' :
            (isPropSelected && hasGameStarted && resultData.result === 'Pending') ? 'Locked' :
            capitalizeFirstLetter(resultData.result);

        const pickStatusColor = pickStatusText === 'No Pick' ? '#CC293C' :
            pickStatusText?.startsWith('Pending') ? '#00AAD1' :
            pickStatusText === 'Locked' ? '#002129' :
            (pickStatusText === 'Win' || pickStatusText === "win") ? '#00AA72' : '#CC293C';

        return { pickStatusText, pickStatusColor };
    }, [isPropSelected, isCurrentSelectionSaved, hasGameStarted, resultData.result, capitalizeFirstLetter]);

    // Memoize dynamic font size function
    const getDynamicFontSize = useCallback((text, baseSize = 16, threshold = isMobile ? 12 : 36, minSize = 10, step = 4, stepReduction = 1) => {
        if (typeof text !== 'string') return baseSize;
    
        const excessLength = text.length - threshold;
    
        if (excessLength <= 0) {
            return baseSize;
        }
    
        const reductions = Math.floor(excessLength / step) * stepReduction;
        const newSize = baseSize - reductions;

        console.log("Text: ", text, "Base Size: ", baseSize, "Threshold: ", threshold, "Min Size: ", minSize, "Step: ", step, "Step Reduction: ", stepReduction, "Excess Length: ", excessLength, "New Size: ", newSize);
    
        return Math.max(minSize, newSize);
    }, [isMobile]);

    // Memoize header font size
    const headerFontSize = useMemo(() => 
        getDynamicFontSize(topHeaderDisplay), 
        [topHeaderDisplay, getDynamicFontSize]
    );

    // Modal handlers
    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => setModalOpen(false);

    // Memoize SVG component
    const unlockedLock = useMemo(() => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            style={{ color: pickStatusData.pickStatusColor, marginLeft: '4px' }}
        >
            <g clipPath="url(#clip0_936_9440)">
                <path d="M4.03846 0C2.76346 0 1.66038 0.529038 0.973269 1.38808C0.287308 2.24769 0 3.36115 0 4.56115V6.34615H1.73077V4.56115C1.73077 3.64962 1.95519 2.93423 2.32558 2.46981C2.69654 2.00596 3.19673 1.73077 4.03846 1.73077C4.8825 1.73077 5.38154 1.99038 5.75135 2.45192C6.12058 2.91346 6.34615 3.64038 6.34615 4.56115V5.19231H8.07692V4.56115C8.07692 3.35423 7.77288 2.22808 7.08519 1.37019C6.39808 0.512308 5.31115 0 4.03846 0ZM5.19231 5.76923C4.23692 5.76923 3.46154 6.54462 3.46154 7.5V13.2692C3.46154 14.2246 4.23692 15 5.19231 15H13.2692C14.2246 15 15 14.2246 15 13.2692V7.5C15 6.54462 14.2246 5.76923 13.2692 5.76923H5.19231ZM9.23077 8.65385C9.53679 8.65385 9.83027 8.77541 10.0467 8.9918C10.263 9.20819 10.3846 9.50167 10.3846 9.80769C10.3846 10.2335 10.1515 10.5992 9.80769 10.7994V12.1154C9.80769 12.4333 9.54865 12.6923 9.23077 12.6923C8.91288 12.6923 8.65385 12.4333 8.65385 12.1154V10.7994C8.31 10.5992 8.07692 10.234 8.07692 9.80769C8.07692 9.50167 8.19849 9.20819 8.41488 8.9918C8.63126 8.77541 8.92475 8.65385 9.23077 8.65385Z" fill={pickStatusData.pickStatusColor} />
            </g>
            <defs>
                <clipPath id="clip0_936_9440">
                    <rect width="15" height="15" fill="white" />
                </clipPath>
            </defs>
        </svg>
    ), [pickStatusData.pickStatusColor]);

    // Memoize icon selection
    const icon = useMemo(() => {
        if (pickStatusData.pickStatusText === 'No Pick' && !hasGameStarted) return '';
        if (pickStatusData.pickStatusText === 'No Pick' && hasGameStarted) return <LockIcon sx={{ color: '#CC293C' }} />;
        if (pickStatusData.pickStatusText.startsWith('Pending')) return unlockedLock;
        if (pickStatusData.pickStatusText === 'Locked') return <LockIcon sx={{ color: pickStatusData.pickStatusColor }} />;
        if (pickStatusData.pickStatusText === 'Win' || pickStatusData.pickStatusText === "win") return <CheckIcon sx={{ color: pickStatusData.pickStatusColor }} />;
        return <CloseIcon sx={{ color: pickStatusData.pickStatusColor }} />;
    }, [pickStatusData.pickStatusText, pickStatusData.pickStatusColor, hasGameStarted, unlockedLock]);

    // Memoize color scheme object
    const topBorderColors = useMemo(() => ({
        notSelectedNotStartedTextColor: customTextColor || "#002129",
        selectedTextColor: "#fff",
        notSelectedStartedTextColor: "#9D9D9D",
        notSelectedBackground: customBackgroundColor || "#C0C0C0",
        selectedUnlockedBackground: "#00AAD1",
        selectedLockedBackground: "#002129",
        selectedWinBackground: "#00AA72",
        selectedLossBackground: "#CC293C", 
        voidedBackground: "#000000"
    }), [customTextColor, customBackgroundColor]);

    // Memoize top border styles calculation
    const topBorderStyles = useMemo(() => {
        let styles = {};
        if (propData.correctOption === "Void") {
            styles = {
                backgroundColor: topBorderColors.voidedBackground,
                textColor: topBorderColors.selectedTextColor
            };
        } else if (!isPropSelected) {
            if (!hasGameStarted) {
                styles = {
                    backgroundColor: topBorderColors.notSelectedBackground,
                    textColor: topBorderColors.notSelectedNotStartedTextColor
                };
            } else {
                styles = {
                    backgroundColor: topBorderColors.selectedLossBackground,
                    textColor: topBorderColors.selectedTextColor
                };
            }
        } else if (!hasGameStarted) {
            styles = {
                backgroundColor: topBorderColors.selectedUnlockedBackground,
                textColor: topBorderColors.selectedTextColor
            };
        } else if (resultData.result === "Pending") {
            styles = {
                backgroundColor: topBorderColors.selectedLockedBackground,
                textColor: topBorderColors.selectedTextColor
            };
        } else if (resultData.result === "Win") {
            styles = {
                backgroundColor: topBorderColors.selectedWinBackground,
                textColor: topBorderColors.selectedTextColor
            };
        } else if (resultData.result === "Loss") {
            styles = {
                backgroundColor: topBorderColors.selectedLossBackground,
                textColor: topBorderColors.selectedTextColor
            };
        }
        return styles;
    }, [propData.correctOption, isPropSelected, hasGameStarted, resultData.result, topBorderColors]);

    // Memoize style objects to prevent inline object creation
    const containerStyles = useMemo(() => ({
        border: propData.highlightProp ? `10px solid ${propData.highlightColor}` : '',
        borderRadius: propData.highlightProp ? '16px' : '',
    }), [propData.highlightProp, propData.highlightColor]);

    const topBorderBoxStyles = useMemo(() => ({
        backgroundColor: topBorderStyles.backgroundColor,
        border: customBorderColor ? `1px solid ${customBorderColor}` : ''
    }), [topBorderStyles.backgroundColor, customBorderColor]);

    const logoBoxStyles = useMemo(() => ({
        borderRight: customBorderColor ? `1px solid ${customBorderColor}` : '1px solid #002129',
        borderLeft: customBorderColor ? `1px solid ${customBorderColor}` : '1px solid #002129',
        alignContent: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '8px 0',
    }), [customBorderColor]);

    const sidesBorderStyles = useMemo(() => ({
        borderRight: customBorderColor ? `1px solid ${customBorderColor}` : '',
        borderLeft: customBorderColor ? `1px solid ${customBorderColor}` : '',
    }), [customBorderColor]);

    const bottomBorderStyles = useMemo(() => ({
        border: customBorderColor ? `1px solid ${customBorderColor}` : '',
    }), [customBorderColor]);

    const infoTextStyles = useMemo(() => ({
        color: (customTextColor && customTextColor !== "#FFFFFF") ? customTextColor : "#002129"
    }), [customTextColor]);

     // Modal styles
     const modalStyles = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? '90%' : '400px',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: '8px',
        textAlign: 'center'
    };

    return (
        <Box 
            className="quick-pick-card-container"
            sx={containerStyles}    
        >
            <Box 
                className="quick-pick-card-top-border"
                sx={topBorderBoxStyles}    
            >
                {!propData.isTiebreaker &&
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography 
                                className="quick-pick-card-top-border-left-text"
                                sx={{ 
                                    color: topBorderStyles.textColor,
                                    fontSize: `${headerFontSize}px` 
                                }}    
                            >
                                {topHeaderDisplay}
                            </Typography>
                            {propData.modalInfo && (
                                <InfoIcon 
                                    sx={{ 
                                        color: topBorderStyles.textColor, 
                                        fontSize: '16px', 
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.7
                                        }
                                    }}
                                    onClick={handleModalOpen}
                                />
                            )}
                        </Box>
                        <Typography 
                            className="quick-pick-card-top-border-right-text"
                            sx={{ color: topBorderStyles.textColor }}    
                        >
                            {propData.correctOption === 'Void' ? 'VOID' : `${propData.points} Point${propData.points > 1 ? 's' : ''}`}
                        </Typography>
                    </>
                }
                {propData.isTiebreaker &&
                    <Typography 
                        className="quick-pick-card-top-border-text"
                        sx={{ color: topBorderStyles.textColor }}    
                    >
                        Tiebreaker Prop (Type In)
                    </Typography>
                }
            </Box>

            {propData.logo &&
                <Box sx={logoBoxStyles}>
                    <img
                        className="quick-pick-card-logo"
                        src={propData.logo}
                        alt="Logo"
                        style={{ maxWidth: '150px', height: 'auto', maxHeight: '100px', objectFit: 'contain' }}
                    />
                </Box>
            }

            {propData.middleDisplay &&
                <MatchUpDisplay 
                    league={propData.league}
                    homeTeamId={propData.homeTeamId}
                    awayTeamId={propData.awayTeamId}
                    middleDisplay={propData.middleDisplay}
                    customBorderColor={customBorderColor}
                />
            }
            <Box 
                className="quick-pick-card-info"
                sx={sidesBorderStyles}
            >
                <Typography 
                    className="quick-pick-card-info-text"
                    sx={infoTextStyles}
                >
                    {propData.propText}
                </Typography>
            </Box>
            {propData.options?.length === 2 &&
                <TwoChoiceCard 
                    pickState={pickState}
                    setPickState={setPickState}
                    interval={interval}
                    hasGameStarted={hasGameStarted}
                    isGameFinal={isGameFinal}
                    prop={prop}
                    selectedGameId={selectedGameId}
                    isPropSelected={isPropSelected}
                    result={resultData.result}
                    customBorderColor={customBorderColor}
                    canUserMakePick={canUserMakePick}
                    setRestrictedModalOpen={setRestrictedModalOpen}
                />
            }
            {propData.options?.length > 2 && propData?.options?.length <= 8 &&
                <MultipleChoiceCard 
                    pickState={pickState}
                    setPickState={setPickState}
                    interval={interval}
                    hasGameStarted={hasGameStarted}
                    isGameFinal={isGameFinal}
                    prop={prop}
                    selectedGameId={selectedGameId}
                    isPropSelected={isPropSelected}
                    result={resultData.result}
                    customBorderColor={customBorderColor}
                    canUserMakePick={canUserMakePick}
                    setRestrictedModalOpen={setRestrictedModalOpen}
                />
            }
            {propData.options?.length > 8 &&
                <DropdownCard
                    pickState={pickState}
                    setPickState={setPickState}
                    interval={interval}
                    hasGameStarted={hasGameStarted}
                    isGameFinal={isGameFinal}
                    prop={prop}
                    selectedGameId={selectedGameId}
                    isPropSelected={isPropSelected}
                    result={resultData.result}
                    customBorderColor={customBorderColor}
                    canUserMakePick={canUserMakePick}
                    setRestrictedModalOpen={setRestrictedModalOpen}
                />
            }
            {propData.options?.length === 1 &&
                <SingleInput
                    pickState={pickState}
                    setPickState={setPickState}
                    interval={interval}
                    hasGameStarted={hasGameStarted}
                    isGameFinal={isGameFinal}
                    prop={prop}
                    selectedGameId={selectedGameId}
                    isPropSelected={isPropSelected}
                    result={resultData.result}
                    customBorderColor={customBorderColor}
                />
            }
            {propData.propTip && !propData.correctOption && 
                <Box
                    className="quick-pick-card-result-container"
                    sx={sidesBorderStyles}
                >
                    <Typography 
                        className="quick-pick-card-result-text"
                        sx={{ color: propTipColor }}
                    >
                        {propData.propTip}
                    </Typography>
                </Box>
            }
            {propData.correctOption &&
                <Box 
                    className="quick-pick-card-result-container"
                    sx={sidesBorderStyles}    
                >
                    <Typography className="quick-pick-card-result-text">
                        Result: {propData.actualTotal ? propData.actualTotal : propData.correctOption === 'Multiple Answers' ? 'Tie' : propData.correctOption}
                    </Typography>
                </Box>
            }
            <Box 
                className="quick-pick-card-bottom-border"
                sx={bottomBorderStyles}
            >
                <Typography className="quick-pick-card-bottom-border-text">
                    {propData.correctOption === 'Void' ? 'This prop is VOID.' : resultData.margin ? 'My Margin:' : 'My Pick:'} 
                </Typography>
                <Typography className="quick-pick-card-bottom-border-text" sx={{ color: pickStatusData.pickStatusColor }}>
                    {propData.correctOption === 'Void' ? '' : resultData.margin ? resultData.margin : pickStatusData.pickStatusText}
                </Typography>
                <Box>
                    {(propData.correctOption === 'Void' || resultData.margin) ? '' : icon}
                </Box>
            </Box>

            {/* Modal for displaying modal info */}
            <Modal
                open={modalOpen}
                onClose={handleModalClose}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box sx={modalStyles}>
                    {propData.logo && (
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <img
                                src={propData.logo}
                                alt="Logo"
                                style={{ 
                                    maxWidth: '100px', 
                                    height: 'auto', 
                                    maxHeight: '80px', 
                                    objectFit: 'contain' 
                                }}
                            />
                        </Box>
                    )}
                    <Typography 
                        id="modal-description" 
                        sx={{ 
                            mb: 3,
                            fontSize: '16px',
                            lineHeight: 1.5,
                            color: '#002129'
                        }}
                    >
                        {propData.modalInfo}
                    </Typography>
                    <Button 
                        variant="contained" 
                        onClick={handleModalClose}
                        sx={{
                            backgroundColor: '#00AAD1',
                            '&:hover': {
                                backgroundColor: '#0088AA'
                            }
                        }}
                    >
                        OK
                    </Button>
                </Box>
            </Modal>
        </Box>
    )
}

export default QuickPickCard;